import React, { useContext, useEffect, useState } from "react";
import { lightGreen } from "resources/colors";
import { turquoise } from "resources/newColors";
import LogoIcon from "resources/images/svg/new-logo.svg";
import showPwdIcon from "resources/images/svg/show-password.svg";
import hidePwdIcon from "resources/images/svg/hide-password.svg";
import {
  Input,
  Button,
  OverlayLayer,
  ErrorDiv,
  Logo,
  SpaceBetweenRow,
  Background,
  Container,
  LoginContainer,
  LogoContainer,
  SiteTitle,
  LoginSubContainer,
  HeaderText,
  InputContainer,
  ButtonContainer,
  InputLabel,
  ContactUsLink,
  SaveLoginsContainer,
  SaveLoginsInner,
  SaveLoginsOuter,
  CenterRow,
  PasswordInput,
  PasswordInputContainer,
  PostfixIcon,
  FooterLinkContainer,
  FooterLink,
} from "./styled";
import { FileLoader } from "views/authenticated/styled";
import { AuthCtx } from "context/AuthContext";
import { BaseModal } from "components/Modals/BaseModal";

const footerLinks = [
  { label: "Eningo.se", link: "https://www.eningo.se" },
  { label: "Kontakt", link: "https://www.eningo.se/kontakt" },
  {
    label: "Användarvillkor",
    link: "https://www.eningo.se/sekretesspolicy",
  },
];

export interface PasswordInputComponentProps {
  value: string;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  onKeyPress: React.KeyboardEventHandler;
  placeholder?: string;
  type: string;
  postfixIcon?: React.ReactElement | React.ReactNode;
}

export const PasswordInputComponent = ({
  type,
  placeholder,
  value,
  onChange,
  onKeyPress,
  postfixIcon,
}: PasswordInputComponentProps) => {
  return (
    <PasswordInputContainer>
      <PasswordInput
        type={type}
        placeholder={placeholder || ""}
        value={value}
        onChange={onChange}
        onKeyPress={onKeyPress}
      />
      {postfixIcon ? postfixIcon : ""}
    </PasswordInputContainer>
  );
};

export interface LoginInputProps {
  label: string;
  value: string;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  onKeyPress: React.KeyboardEventHandler;
  toggleSaveLogins?: () => void;
  saveLogins?: boolean;
  placeholder?: string;
  type: string;
  postfixIcon?: React.ReactElement | React.ReactNode;
}

export const LoginInput = ({
  label,
  value,
  onChange,
  onKeyPress,
  placeholder,
  type,
  saveLogins,
  toggleSaveLogins,
  postfixIcon,
}: LoginInputProps) => {
  return (
    <InputContainer>
      {label === "Användarnamn" && <InputLabel>{label}</InputLabel>}
      {label === "Lösenord" && (
        <SpaceBetweenRow>
          <InputLabel>{label}</InputLabel>
        </SpaceBetweenRow>
      )}
      {label === "Lösenord" ? (
        <PasswordInputComponent
          type={type}
          placeholder={placeholder || ""}
          value={value}
          onChange={onChange}
          onKeyPress={onKeyPress}
          postfixIcon={postfixIcon}
        />
      ) : (
        <Input
          type={type}
          placeholder={placeholder || ""}
          value={value}
          onChange={onChange}
          onKeyPress={onKeyPress}
        />
      )}
      {label === "Lösenord" && (
        <SaveLoginsContainer>
          <SaveLoginsOuter onClick={() => toggleSaveLogins()}>
            <SaveLoginsInner
              backgroundColor={saveLogins ? turquoise : "#fff"}
            />
          </SaveLoginsOuter>
          Kom ihåg mig
        </SaveLoginsContainer>
      )}
    </InputContainer>
  );
};

const LoginView = (): React.ReactElement => {
  // State
  const [email, setEmail] = useState<string | null>("");
  const [password, setPassword] = useState<string | null>("");
  const [error, setError] = useState<string | null>(null);
  const [storeLogins, setStoreLogins] = useState<boolean>(false);
  const [loginActive, setLoginActive] = useState<boolean>(false);
  const [isRevealPwd, setIsRevealPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSSOModal, setShowSSOModal] = useState(false);
  // Actions

  const { login, loginSSO } = useContext(AuthCtx);

  const signIn = async () => {
    // Check credentials
    setLoading(true);
    setError("");
    if (email === "" || password === "") {
      setError("Ett eller flera fält är tomma.");
      setLoading(false);
      return;
    }

    // Attempt login

    setLoginActive(true);
    try {
      const success = await login(email, password);
      if (!success) setError("Ogiltig e-postadress eller ogiltigt lösenord");
    } catch (error) {
      setLoading(false);
      setError("Ogiltig e-postadress eller ogiltigt lösenord");
    }
    setLoading(false);
  };

  const onKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      signIn();
    }
  };
  useEffect(() => {
    if (error)
      setTimeout(() => {
        setError("");
      }, 8000);
  }, [error]);

  return (
    <Background>
      <OverlayLayer>
        <Container>
          <LoginContainer>
            {showSSOModal && (
              <BaseModal
                show={showSSOModal}
                setShow={setShowSSOModal}
                title="Logga in för att fortsätta"
                body={
                  <ButtonContainer>
                    <Button
                      color={"#ffffff"}
                      hoverColor={"#e2e2e2"}
                      css={`
                        width: 60%;
                        height: 50px;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        border: 2px solid #ccc;
                        border-radius: 6px;
                        color: #333;
                      `}
                      onClick={async () => {
                        await loginSSO();
                      }}
                    >
                      <img
                        src="https://kungsbacka.se/images/18.68e39ecb17ede8bc2d932e18/1645197514711/600px_Kba_logo18_4-f%C3%A4rg.png"
                        alt="Kungsbacka Kommun Logo"
                        style={{
                          width: "45px",
                          height: "45px",
                          marginRight: "15px",
                        }}
                      />{" "}
                      Kungsbacka Kommun
                    </Button>
                  </ButtonContainer>
                }
              />
            )}
            <LogoContainer>
              <Logo alt="logo" src={LogoIcon} />
              <SiteTitle>Eningo</SiteTitle>
            </LogoContainer>
            <LoginSubContainer>
              <HeaderText>Logga in</HeaderText>
              {error && <ErrorDiv>{error}</ErrorDiv>}
              <LoginInput
                label="Användarnamn"
                type="email"
                placeholder="Ange användarnamn"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyPress={onKeyPress}
              />
              <LoginInput
                label="Lösenord"
                type={isRevealPwd ? "text" : "password"}
                placeholder="Ange lösenord"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={onKeyPress}
                toggleSaveLogins={() => setStoreLogins(!storeLogins)}
                saveLogins={storeLogins}
                postfixIcon={
                  <PostfixIcon
                    css={"width: 17px; height: auto"}
                    title={isRevealPwd ? "Dölj lösenord" : "Visa lösenord"}
                    src={isRevealPwd ? hidePwdIcon : showPwdIcon}
                    onClick={() => setIsRevealPwd((prevState) => !prevState)}
                  />
                }
              />
              <ButtonContainer>
                <Button
                  color={turquoise}
                  hoverColor={lightGreen}
                  css={
                    "width: 60%; height: 40px;display: flex;justify-content: center;align-items: center;"
                  }
                  onClick={signIn}
                >
                  {loading ? <FileLoader /> : "Logga in"}
                </Button>
              </ButtonContainer>
              <CenterRow css={"margin-top: 20px; margin-bottom: 10px;"}>
                <InputLabel>eller</InputLabel>
              </CenterRow>

              <ButtonContainer>
                <Button
                  color={"#ffffff"}
                  hoverColor={"#f0f0f0"}
                  css={`
                    width: 60%;
                    height: 40px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    border: 1px solid #ccc;
                    color: #333;
                  `}
                  onClick={() => setShowSSOModal(true)}
                >
                  Logga in med SSO
                </Button>
              </ButtonContainer>
              <CenterRow css={"margin-top: 20px"}>
                <InputLabel>Har du inget konto?</InputLabel>
                <ContactUsLink
                  to={"https://www.eningo.se/kontakt"}
                  target="_blank"
                >
                  {" "}
                  Kontakta oss
                </ContactUsLink>
              </CenterRow>
              <FooterLinkContainer>
                {footerLinks.map((fl, i) => (
                  <FooterLink
                    key={i}
                    to={fl.link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {fl.label}
                  </FooterLink>
                ))}
              </FooterLinkContainer>
            </LoginSubContainer>
          </LoginContainer>
        </Container>
      </OverlayLayer>
    </Background>
  );
};

export default LoginView;
